// Web Push enable/disable from the browser. Registers the service
// worker, subscribes via PushManager with the server's VAPID key, and
// posts the subscription to the backend. Every step degrades to a
// thrown Error with a short `.reason` the UI can show — the channel
// preference is saved regardless, so push "wants on" even if the
// browser can't subscribe yet (unsupported browser, blocked
// permission, VAPID keys not configured server-side).

import { api } from './api.js';

// The service worker is built into the app bundle (Vite base '/app/') and
// served at /app/sw.js — NOT the site root. Registering '/sw.js' returns
// the SPA's 404 HTML, which the browser refuses as a worker, so no push
// subscription is ever created. Register the real URL; its scope (/app/)
// covers every page in the app.
const SW_URL = '/app/sw.js';

export function pushSupported() {
  return (
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    typeof window !== 'undefined' &&
    'PushManager' in window &&
    'Notification' in window
  );
}

// "Phone & browser alerts" off on ONE device unsubscribes that browser
// only; it used to switch push off on the account, so turning off laptop
// popups silenced the phone too, power-loss page included. This flag
// remembers the choice here, so the quiet re-subscribe on load does not
// undo it. A blocked or private browser just forgets.
const OFF_HERE_KEY = 'aqs.pushOffHere';

export function pushOffOnThisDevice() {
  try {
    return localStorage.getItem(OFF_HERE_KEY) === '1';
  } catch {
    return false;
  }
}

function setPushOffHere(off) {
  try {
    if (off) localStorage.setItem(OFF_HERE_KEY, '1');
    else localStorage.removeItem(OFF_HERE_KEY);
  } catch {
    /* storage blocked: the choice is not remembered */
  }
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function fail(reason, message) {
  const e = new Error(message || reason);
  e.reason = reason;
  return e;
}

// Returns the active push subscription, or null. Used to reflect real
// state in the UI rather than trusting the saved preference alone.
export async function getCurrentPushSubscription() {
  if (!pushSupported()) return null;
  try {
    const reg = await navigator.serviceWorker.getRegistration(SW_URL);
    if (!reg) return null;
    return await reg.pushManager.getSubscription();
  } catch {
    return null;
  }
}

// Subscribe this browser. Throws fail('unsupported' | 'denied' |
// 'dismissed' | 'not_configured' | 'subscribe_failed'). 'dismissed' is a
// prompt closed without an answer: asking again works, whereas 'denied'
// needs the device's settings. Pass { force: true } to drop
// any existing subscription and mint a fresh one — the "Refresh push"
// path, so a stale or server-forgotten subscription gets re-created.
export async function enableWebPush({ force = false } = {}) {
  if (!pushSupported()) throw fail('unsupported', 'This browser does not support web push.');

  const permission = await Notification.requestPermission();
  if (permission === 'denied') {
    throw fail('denied', 'Notification permission was denied.');
  }
  if (permission !== 'granted') {
    throw fail('dismissed', 'Notification permission was not answered.');
  }

  // Server VAPID public key — 503 when push isn't configured.
  let publicKey;
  try {
    ({ publicKey } = await api('/api/push/vapid-key'));
  } catch {
    throw fail('not_configured', 'Push is not configured on the server yet.');
  }
  if (!publicKey) throw fail('not_configured', 'Push is not configured on the server yet.');

  const reg = await navigator.serviceWorker.register(SW_URL);
  // Never hang on `.ready`: if activation stalls (or a prior broken
  // registration left no active worker) this used to wait forever and
  // freeze the caller's busy state. Cap the wait, then proceed — the
  // subscribe() below rejects cleanly if there's genuinely no worker.
  await Promise.race([
    navigator.serviceWorker.ready,
    new Promise((resolve) => setTimeout(resolve, 6000)),
  ]);

  let sub = await reg.pushManager.getSubscription();
  if (sub && force) {
    await sub.unsubscribe().catch(() => {});
    sub = null;
  }
  if (!sub) {
    try {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
    } catch (e) {
      throw fail('subscribe_failed', e?.message || 'Could not subscribe this browser.');
    }
  }

  const json = sub.toJSON();
  await api('/api/push/subscribe', {
    method: 'POST',
    body: { endpoint: json.endpoint, keys: json.keys },
  });
  setPushOffHere(false);
  return true;
}

// Unsubscribe this browser + tell the backend to revoke the row.
// Best-effort; never throws. `remember` marks this device as switched off
// by the keeper (see pushOffOnThisDevice).
export async function disableWebPush({ remember = false } = {}) {
  if (remember) setPushOffHere(true);
  try {
    const sub = await getCurrentPushSubscription();
    if (sub) {
      const endpoint = sub.endpoint;
      await sub.unsubscribe().catch(() => {});
      await api('/api/push/subscribe', { method: 'DELETE', body: { endpoint } }).catch(() => {});
    }
  } catch {
    /* best effort */
  }
}
